/**
 * État de l'application : le tracé chargé, le scénario (gares + réglages), la
 * sélection, et le résultat du modèle recalculé à chaque changement (~1 ms).
 * Le scénario est miroité dans le hash de l'URL à chaque modification.
 */
import { create } from "zustand";
import type { RoadTimes } from "../charts/series";
import { presetStops, type PresetId } from "../model/presets";
import { communeAt, type RouteData, type RouteIndex } from "../model/route";
import { DEFAULT_SETTINGS, runScenario, type RunResult, type RunSettings } from "../model/run";
import { decodeScenario, encodeScenario, type ScenarioState } from "./url";

const BASE = import.meta.env.BASE_URL;
const REFERENCE_ID = "SD-2979_O-3309";

export type Onglet = "gares" | "profil" | "espace" | "methode";

export interface AppState {
  route: RouteData | null;
  routeIndex: RouteIndex | null;
  roads: RoadTimes | null;
  error: string | null;
  scenario: ScenarioState;
  result: RunResult | null;
  /** résultat du scénario de référence, pour la comparaison */
  reference: RunResult | null;
  selected: number | null;
  dragging: number | null;
  /** point du tracé survolé dans un graphique, pour le repère sur la carte */
  hoverIdx: number | null;
  onglet: Onglet;
  reglagesOuverts: boolean;
  detailOuvert: boolean;

  load(): Promise<void>;
  addStop(idx: number): void;
  moveStop(from: number, to: number): void;
  removeStop(idx: number): void;
  renameStop(idx: number, name: string): void;
  applyPreset(p: PresetId): void;
  setSettings(patch: Partial<RunSettings>): void;
  select(idx: number | null): void;
  setDragging(idx: number | null): void;
  setHover(idx: number | null): void;
  setOnglet(o: Onglet): void;
  toggleReglages(): void;
  toggleDetail(): void;
  stationName(idx: number): string;
  isTerminus(idx: number): boolean;
}

let lastHash = "";
function writeHash(sc: ScenarioState) {
  const h = "#" + encodeScenario(sc, REFERENCE_ID);
  if (h === lastHash) return;
  lastHash = h;
  history.replaceState(null, "", h);
}

export const useApp = create<AppState>()((set, get) => {
  /** Applique un nouveau scénario : recalcul + URL. */
  const commit = (scenario: ScenarioState) => {
    const { route } = get();
    const result = route ? runScenario(route, scenario.stops, scenario.settings) : null;
    set({ scenario, result });
    writeHash(scenario);
  };

  return {
    route: null,
    routeIndex: null,
    roads: null,
    error: null,
    scenario: { routeId: REFERENCE_ID, stops: [], names: {}, settings: { ...DEFAULT_SETTINGS } },
    result: null,
    reference: null,
    selected: null,
    dragging: null,
    hoverIdx: null,
    onglet: "gares",
    reglagesOuverts: false,
    detailOuvert: false,

    async load() {
      try {
        const fromUrl = decodeScenario(location.hash, REFERENCE_ID);
        const routeId = fromUrl?.routeId ?? REFERENCE_ID;
        const [index, route] = await Promise.all([
          fetch(`${BASE}data/routes/index.json`).then((r) => r.json() as Promise<RouteIndex>),
          fetch(`${BASE}data/routes/${routeId}.v1.json`).then((r) => {
            if (!r.ok) throw new Error(`tracé ${routeId} introuvable`);
            return r.json() as Promise<RouteData>;
          }),
        ]);
        // temps routiers : facultatifs, l'onglet espace-temps s'en passe
        const roads = await fetch(`${BASE}data/road-times.json`)
          .then((r) => (r.ok ? (r.json() as Promise<RoadTimes>) : null))
          .catch(() => null);
        const settings = fromUrl?.settings ?? { ...DEFAULT_SETTINGS };
        const n = route.coords.length;
        const stops =
          fromUrl?.stops && fromUrl.stops.length >= 2
            ? fromUrl.stops.filter((s) => s >= 0 && s < n)
            : presetStops(route, "reference");
        const scenario: ScenarioState = { routeId: route.id, stops, names: fromUrl?.names ?? {}, settings };
        const reference = runScenario(route, presetStops(route, "reference"), settings);
        set({ route, routeIndex: index, roads, reference });
        commit(scenario);
        window.addEventListener("hashchange", () => {
          if (location.hash === lastHash) return;
          const d = decodeScenario(location.hash, REFERENCE_ID);
          if (d?.stops && d.stops.length >= 2) {
            commit({ routeId: route.id, stops: d.stops, names: d.names ?? {}, settings: d.settings ?? settings });
          }
        });
      } catch (e) {
        set({ error: e instanceof Error ? e.message : String(e) });
      }
    },

    addStop(idx) {
      const { scenario } = get();
      if (scenario.stops.includes(idx)) {
        set({ selected: idx });
        return;
      }
      commit({ ...scenario, stops: [...scenario.stops, idx].sort((a, b) => a - b) });
      set({ selected: idx });
    },

    moveStop(from, to) {
      const { scenario } = get();
      if (from === to || scenario.stops.includes(to)) return;
      const stops = scenario.stops.map((s) => (s === from ? to : s)).sort((a, b) => a - b);
      const names = { ...scenario.names };
      if (names[from] !== undefined) {
        names[to] = names[from];
        delete names[from];
      }
      commit({ ...scenario, stops, names });
      set((st) => ({ selected: st.selected === from ? to : st.selected }));
    },

    removeStop(idx) {
      const { scenario } = get();
      if (get().isTerminus(idx)) return;
      const names = { ...scenario.names };
      delete names[idx];
      commit({ ...scenario, stops: scenario.stops.filter((s) => s !== idx), names });
      set((st) => ({ selected: st.selected === idx ? null : st.selected }));
    },

    renameStop(idx, name) {
      const { scenario } = get();
      const names = { ...scenario.names };
      if (name.trim() === "") delete names[idx];
      else names[idx] = name.slice(0, 60);
      commit({ ...scenario, names });
    },

    applyPreset(p) {
      const { route, scenario } = get();
      if (!route) return;
      commit({ ...scenario, stops: presetStops(route, p), names: {} });
      set({ selected: null });
    },

    setSettings(patch) {
      const { scenario, route } = get();
      const settings = { ...scenario.settings, ...patch };
      if (route) set({ reference: runScenario(route, presetStops(route, "reference"), settings) });
      commit({ ...scenario, settings });
    },

    select: (idx) => set({ selected: idx }),
    setDragging: (idx) => set({ dragging: idx }),
    setHover: (idx) => set((st) => (st.hoverIdx === idx ? st : { hoverIdx: idx })),
    setOnglet: (o) => set({ onglet: o }),
    toggleReglages: () => set((st) => ({ reglagesOuverts: !st.reglagesOuverts })),
    toggleDetail: () => set((st) => ({ detailOuvert: !st.detailOuvert, onglet: "gares" })),

    stationName(idx) {
      const { scenario, route } = get();
      const custom = scenario.names[idx];
      if (custom) return custom;
      const ref = route?.reference_stations.find((s) => s.idx === idx);
      if (ref) return ref.name;
      return (route && communeAt(route, idx)) ?? `PK ${((idx * (route?.step_m ?? 25)) / 1000).toFixed(1)}`;
    },

    isTerminus(idx) {
      const { route } = get();
      if (!route) return false;
      const s = route.reference_stations;
      return idx === s[0].idx || idx === s[s.length - 1].idx;
    },
  };
});
